import { ContainerCopyBeforeDeleteService } from '@affine/core/components/delete-with-copy/container-copy-before-delete.service';
import { type Framework } from '@toeverything/infra';

import {
  DefaultServerService,
  ServersService,
  WorkspaceServerService,
} from '../cloud';
import { WorkspaceDialogService } from '../dialogs/services/workspace-dialog';
import { DocCreateMiddleware, DocScope, DocService, DocsService } from '../doc';
import { AccessibleDocCacheStore } from '../doc/stores/accessible-doc-cache';
import { DocsStore } from '../doc/stores/docs';
import { DocsSearchService } from '../docs-search';
import { EditorScope, EditorService } from '../editor';
import { WorkspaceAreaCacheStore } from '../folder-api';
import { OfflineMutationOutboxService } from '../offline-mutation-outbox';
import { AccessibleFolderCacheStore } from '../organize';
import { GuardService } from '../permissions';
import { ExtensionSidebarService, Workbench } from '../workbench';
import { WorkspaceScope, WorkspaceService } from '../workspace';
import {
  configureWorkspaceLocalCacheModule,
  WorkspaceLocalCacheService,
} from '../workspace-local-cache';
import {
  CalendarApiClientService,
  CalendarAppService,
  CalendarLocalProjectionService,
  CalendarMutationOutboxService,
  PlannerPresentationSettingsService,
} from './calendar';
import { configureWorkspaceViewContextModule } from './copilot/workspace-view-context';
import {
  EdgeSidebarSetup,
  ReactiveEdgeService,
} from './edge-infrastructure/services';
import { MailComposeHandoffService } from './mail/mail-compose-handoff';
import { MailMutationOutboxService } from './mail/mail-mutation-outbox';
import { AppBaseLifecycleOutboxService } from './services/app-base-lifecycle-outbox';
import { AppSettingsOfflineCopyService } from './services/app-settings-offline-copy';
import { AppSettingsRowStoreService } from './services/app-settings-row-store';
import { AutomationService } from './services/automation';
import { AutomationAutoSaveService } from './services/automation-auto-save';
import { AutomationConfigApiService } from './services/automation-config-api';
import { AutomationDeletionService } from './services/automation-deletion';
import { AutomationDiscoveryService } from './services/automation-discovery';
import { AutomationSocketService } from './services/automation-socket';
import { BlockDeletionPresentationService } from './services/block-deletion-presentation';
import { ComponentRegistry } from './services/component-registry';
import { ConfigProvisionerService } from './services/config-provisioner.service';
import { ContentDbSlugValidator } from './services/content-db-slug-validator';
import { CrossDocAutomationService } from './services/cross-doc-automation';
import { CrossDocRelationService } from './services/cross-doc-relation';
import { DatabaseDeletionService } from './services/database-deletion';
import { DatabaseDiscoveryService } from './services/database-discovery';
import { DatabaseMoveOutboxService } from './services/database-move-outbox';
import { DocTrashService } from './services/doc-trash';
import { DocViewStateService } from './services/doc-view-state';
import { ElsterService } from './services/elster';
import { StreamLainEventBus } from './services/event-bus';
import { StreamLainEventRouter } from './services/event-router';
import { ExecutionService } from './services/execution';
import { FileIndexDocCreateMiddleware } from './services/file-index-doc-create-middleware';
import { FullscreenRegistry } from './services/fullscreen-registry';
import { LoggingService } from './services/logging';
import { MarketplaceService } from './services/marketplace';
import { PublishEditorState } from './services/publish-editor-state';
import { ReviewService } from './services/review';
import { StreamLainSidebarSetup } from './services/sidebar-setup';
import { ViewDataSourceFactory } from './services/view-datasource-factory';
import { WebhookService } from './services/webhook';
import { WorkflowService } from './services/workflow';
import { WorkflowClientService } from './services/workflow-client';
import { WorkflowTestContextService } from './services/workflow-test-context';
import { YjsViewDataSource } from './services/yjs-view-datasource';
import { ViewConfigStore } from './stores/view-config.store';
import { TutorialLauncher } from './tutorial/services/tutorial-launcher';
import { TutorialPersistence } from './tutorial/services/tutorial-persistence';
import { TutorialRegistry } from './tutorial/services/tutorial-registry';
import {
  DatabaseViewLocalDiscoveryService,
  ViewBackedAppApiClientService,
  ViewBackedAppMutationOutboxService,
  ViewBackedAppRegistryService,
  ViewBackedAppService,
} from './view-backed-app';

/**
 * Konfiguriert das StreamLain Module.
 * Muss 1x in modules/index.ts aufgerufen werden.
 */
export function configureStreamLainModule(framework: Framework): void {
  configureWorkspaceLocalCacheModule(framework);
  configureWorkspaceViewContextModule(framework);
  framework
    .scope(WorkspaceScope)
    .service(AutomationService)
    .service(CrossDocAutomationService, [DocsService, GuardService])
    .service(CrossDocRelationService, [DocsService, GuardService])
    .service(DatabaseDiscoveryService, [
      DocsService,
      GuardService,
      WorkspaceService,
      WorkspaceServerService,
      DocsSearchService,
    ])
    .service(AutomationDiscoveryService, [DocsService, GuardService])
    .service(AppBaseLifecycleOutboxService, [
      WorkspaceServerService,
      OfflineMutationOutboxService,
      AccessibleFolderCacheStore,
      AccessibleDocCacheStore,
      ContainerCopyBeforeDeleteService,
    ])
    .service(DatabaseMoveOutboxService, [
      WorkspaceService,
      WorkspaceServerService,
      OfflineMutationOutboxService,
      WorkspaceDialogService,
    ])
    .service(BlockDeletionPresentationService, [
      WorkspaceService,
      OfflineMutationOutboxService,
    ])
    .service(DatabaseDeletionService, [
      WorkspaceService,
      WorkspaceServerService,
      OfflineMutationOutboxService,
      DatabaseDiscoveryService,
      BlockDeletionPresentationService,
    ])
    .service(AutomationDeletionService, [
      WorkspaceService,
      WorkspaceServerService,
      OfflineMutationOutboxService,
      BlockDeletionPresentationService,
      WorkflowService,
      ContainerCopyBeforeDeleteService,
    ])
    .service(DocTrashService, [
      WorkspaceServerService,
      DatabaseDeletionService,
      AutomationDeletionService,
    ])
    .service(LoggingService)
    .service(ExecutionService, [LoggingService])
    // sl-trigger-removal.5: TriggerService and TriggerCleanupService removed
    // sl-n8i1: WorkflowClientService requires WorkspaceService for workspace-scoped credentials
    .service(WorkflowClientService, [WorkspaceService])
    .service(WorkflowService, [WorkflowClientService])
    .service(MarketplaceService, [WorkflowClientService])
    .service(WebhookService)
    .service(ReviewService) // S020-8.20.5
    // sl-automation-refactor.4: Config API Services
    .service(AutomationConfigApiService)
    .service(WorkflowTestContextService, [AutomationConfigApiService])
    .service(AutomationAutoSaveService, [AutomationConfigApiService])
    .service(AutomationSocketService, [
      WorkspaceService,
      WorkspaceServerService,
      LoggingService,
      ExecutionService,
      DocsService,
    ])
    .impl(
      DocCreateMiddleware('streamlain-file-index'),
      FileIndexDocCreateMiddleware,
      [DocsStore]
    )
    .service(ElsterService)
    .service(MailMutationOutboxService, [
      WorkspaceServerService,
      OfflineMutationOutboxService,
    ])
    .service(MailComposeHandoffService)
    .service(CalendarApiClientService, [
      WorkspaceServerService,
      WorkspaceService,
    ])
    .service(ViewBackedAppApiClientService, [WorkspaceServerService])
    .service(ViewBackedAppRegistryService)
    .service(DatabaseViewLocalDiscoveryService, [
      DocsService,
      WorkspaceAreaCacheStore,
      AccessibleFolderCacheStore,
    ])
    .service(CalendarLocalProjectionService, [
      DocsService,
      DatabaseViewLocalDiscoveryService,
      DefaultServerService,
    ])
    .service(ViewBackedAppMutationOutboxService, [
      WorkspaceService,
      ViewBackedAppApiClientService,
      OfflineMutationOutboxService,
      AccessibleDocCacheStore,
      ViewBackedAppRegistryService,
    ])
    .service(ViewBackedAppService, [
      ViewBackedAppApiClientService,
      ViewBackedAppMutationOutboxService,
    ])
    .service(CalendarMutationOutboxService, [
      WorkspaceService,
      CalendarApiClientService,
      OfflineMutationOutboxService,
      ViewBackedAppMutationOutboxService,
    ])
    .service(CalendarAppService, [
      DocsService,
      CalendarApiClientService,
      CalendarLocalProjectionService,
      CalendarMutationOutboxService,
      ViewBackedAppService,
    ])
    .service(PlannerPresentationSettingsService, [
      WorkspaceService,
      WorkspaceServerService,
      DefaultServerService,
      ServersService,
      WorkspaceLocalCacheService,
    ])
    .service(FullscreenRegistry)
    .service(DocViewStateService)
    // sl-9qm.4.1: Component Registry (workspace-wide, shared across docs)
    .service(ComponentRegistry)
    // sl-oll9.15: Typed Event Bus
    .service(StreamLainEventBus)
    .service(StreamLainSidebarSetup, [
      ExtensionSidebarService,
      FullscreenRegistry,
      ComponentRegistry,
    ])
    // sl-oll9.13: Event routing extracted from SidebarSetup
    .service(StreamLainEventRouter, [
      AutomationService,
      Workbench,
      FullscreenRegistry,
    ])
    .service(EdgeSidebarSetup)
    // sl-728o.1: Tutorial System
    .service(TutorialPersistence)
    .service(TutorialRegistry, [TutorialPersistence])
    // sl-728o.7.1: Tutorial Launcher (handles card start events + RBAC)
    .service(TutorialLauncher, [TutorialRegistry]);

  // sl-u4d2: DemoDocService removed — onboarding doc is backend-seeded
  // sl-9qm.3.3: Publish Editor state (selection + drag)
  framework.scope(WorkspaceScope).scope(DocScope).service(PublishEditorState);
  // sl-8n5m.8.4: ContentDB Slug-Conflict Validator (stateless, workspace scope)
  framework.scope(WorkspaceScope).service(ContentDbSlugValidator);
  // The Config tables of every opened App Container stay available offline.
  framework
    .scope(WorkspaceScope)
    .service(AppSettingsOfflineCopyService, [WorkspaceService]);
  framework
    .scope(WorkspaceScope)
    .service(AppSettingsRowStoreService, [
      WorkspaceService,
      DocsService,
      AppSettingsOfflineCopyService,
    ]);
  // sl-bpu6.4: ConfigProvisioner — atomic ConfigDB + ContentDB + first row
  framework
    .scope(WorkspaceScope)
    .service(ConfigProvisionerService, [DocsService, WorkspaceServerService]);
  // sl-9qm.3.4: ViewConfig Y.js Store (collaborative config storage)
  framework
    .scope(WorkspaceScope)
    .scope(DocScope)
    .store(ViewConfigStore, [DocService]);
  // sl-9qm.5.1: Y.js Data Source (builder + internal mode)
  framework.scope(WorkspaceScope).service(YjsViewDataSource, [DocsService]);
  // sl-9qm.5.2: Data Source Factory (selects Yjs/Materialized/Prisma)
  framework
    .scope(WorkspaceScope)
    .service(ViewDataSourceFactory, [YjsViewDataSource]);

  // E018-S009: Reactive edge system in EditorScope
  framework
    .scope(WorkspaceScope)
    .scope(DocScope)
    .scope(EditorScope)
    .service(ReactiveEdgeService, [
      EditorService,
      EdgeSidebarSetup,
      DatabaseDiscoveryService,
      AutomationDiscoveryService,
      DocService,
    ]);
}
